import { Locale } from "@redotech/crdb-enums/common/locale";
import {
  FLOW_ELEMENT_TRANSLATION_TEXT_LIMIT,
  FlowElementTranslationDelete,
  FlowElementTranslationWrite,
} from "@redotech/merchant-sdk/rpc/schema/return-flow-translations/set-flow-element-translations";
import { TranslatableFieldSpec } from "@redotech/redo-merchant-app-common/components/translations/translation-field-editors";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { FlowElementTranslatableField } from "@redotech/redo-return-core-common/return-flow/flow-element-translation";

export type FlowTranslatableFieldSpec = TranslatableFieldSpec & {
  field: FlowElementTranslatableField;
};

export const languageFieldKey = (language: string, field: string) =>
  `${language} ${field}`;

/** Missing ai attribution here would rewrite reloaded AI translations as merchant. */
export function indexTranslations(
  translations: readonly {
    language: string;
    field: string;
    text: string;
    source: string;
    sourceText?: string | null;
  }[],
): {
  values: Record<string, string>;
  aiKeys: ReadonlySet<string>;
  sourceTexts: Record<string, string>;
} {
  const values: Record<string, string> = {};
  const aiKeys = new Set<string>();
  const sourceTexts: Record<string, string> = {};
  for (const translation of translations) {
    const key = languageFieldKey(translation.language, translation.field);
    values[key] = translation.text;
    if (translation.source === "ai") {
      aiKeys.add(key);
    }
    if (translation.sourceText != null) {
      sourceTexts[key] = translation.sourceText;
    }
  }
  return { values, aiKeys, sourceTexts };
}

export function boundedSourceText(value: string): string | undefined {
  return value && value.length <= FLOW_ELEMENT_TRANSLATION_TEXT_LIMIT
    ? value
    : undefined;
}

/** Carries meaning without contributing text, so markup holding one is not empty. */
const EMBED_TAG = /<(img|video|iframe|embed|object|svg)\b/i;

/** A cleared rich-text editor still emits markup (`<p><br></p>`) that must save as a delete. */
export function normalizeTranslation(
  kind: TranslatableFieldSpec["kind"],
  raw: string,
): string {
  const value = raw.trim();
  if (kind !== "html" || EMBED_TAG.test(value)) {
    return value;
  }
  return value.replace(/<[^>]*>/g, "").trim() === "" ? "" : value;
}

/**
 * A changed non-empty value is a write, a value cleared from something
 * previously saved is a delete, and anything unchanged is omitted.
 */
export function computeTranslationSaveDiff({
  elementId,
  flowType,
  fields,
  languages,
  values,
  savedValues,
  savedSourceTexts,
  aiKeys,
}: {
  elementId: string;
  flowType: FlowType;
  fields: FlowTranslatableFieldSpec[];
  languages: readonly Locale[];
  values: Record<string, string>;
  savedValues: Record<string, string>;
  savedSourceTexts?: Record<string, string>;
  aiKeys: ReadonlySet<string>;
}): {
  writes: FlowElementTranslationWrite[];
  deletes: FlowElementTranslationDelete[];
} {
  const writes: FlowElementTranslationWrite[] = [];
  const deletes: FlowElementTranslationDelete[] = [];
  for (const field of fields) {
    for (const language of languages) {
      const key = languageFieldKey(language, field.field);
      const value = normalizeTranslation(field.kind, values[key] ?? "");
      const savedValue = normalizeTranslation(
        field.kind,
        savedValues[key] ?? "",
      );
      const currentSource = normalizeTranslation(field.kind, field.source);
      const savedSource = savedSourceTexts?.[key];
      const drifted =
        savedSource !== undefined && savedSource !== currentSource;
      if (value === savedValue && !(value && drifted)) {
        continue;
      }
      if (value) {
        writes.push({
          elementId,
          field: field.field,
          language,
          text: value,
          source: aiKeys.has(key) ? "ai" : "merchant",
          sourceText: boundedSourceText(currentSource),
          flowType,
        });
      } else {
        deletes.push({ elementId, field: field.field, language });
      }
    }
  }
  return { writes, deletes };
}
